// ============================================================
// BOSCO — i18n (i18n.js)
//
// Trois responsabilités côté front :
//
// 1. Mapping classes Foundry → slug canonique Bosco. Le compendium dnd5e
//    sort des noms de classe en français OU anglais selon les versions ;
//    on accepte les deux variantes (peu importe accent ou casse) et on
//    retombe sur un slug canonique en français normalisé sans accent,
//    utilisé par le CSS via l'attribut data-class.
//
// 2. Traduction des libellés UI (pills, boutons, états…). La langue active
//    est déterminée au boot dans cet ordre : (a) `users.locale` lue via
//    `GET /api/moi` si l'user est connecté, (b) fallback `navigator.language`
//    sinon (FR si le navigateur commence par 'fr', EN par défaut).
//
// 3. Application au DOM via `appliquerI18n(racine)` qui parcourt les
//    attributs `data-i18n="cle"` (remplace textContent) et
//    `data-i18n-attr="attr:cle[;attr2:cle2…]"` (remplace l'attribut).
//    Auto-appelé sur `document` une fois le dict chargé. À rappeler
//    manuellement sur tout DOM injecté dynamiquement après le boot.
//
// Source de vérité : src/public/lang/fr.json et en.json, format flat
// clé/valeur avec clés `BOSCO.<Categorie>.<id>` (calqué sur le module
// Bosco Companion).
//
// Usage côté front (script global, à charger avant les autres scripts) :
//   await window.I18nPrete;                   // attendre le chargement initial
//   slugCanoniqueClasse('Bard');              // → 'barde'
//   slugCanoniqueClasse('Rôdeur');            // → 'rodeur'
//   slugCanoniqueClasse('Prêtre');            // → 'clerc'
//   slugCanoniqueClasse('Truc inconnu');      // → '' (caller décide du fallback)
//   traduire('BOSCO.Action.Use', 'Utiliser'); // → 'Use' en EN, 'Utiliser' en FR
//   appliquerI18n(carteDom);                  // traduit data-i18n / data-i18n-attr
// ============================================================

(function () {
    'use strict';

    // Alias de noms Foundry qui ne sont pas dans les JSON canoniques mais
    // qu'on accepte quand même (typos de modules, traductions alternatives).
    // Clés et valeurs déjà normalisées (minuscule, sans accent).
    //
    // `pretre` : ancienne traduction FR du Clerc (compendium dnd5e v2),
    // garde un fallback pour les snapshots non re-poussés.
    // `sorcier` : ancienne traduction FR du Warlock (le compendium dnd5e
    // 2024 utilise « Occultiste » que le dict FR affiche désormais). Notre
    // slug canonique interne reste `sorcier` pour la cohérence CSS et BDD
    // historique ; on accepte donc « Sorcier » Foundry en alias explicite.
    const ALIAS_SUPPLEMENTAIRES = {
        pretre: 'clerc',
        sorcier: 'sorcier',
    };

    const LOCALES_VALIDES = new Set(['fr', 'en']);

    // Détection de la langue active. Priorité à `users.locale` (persisté en
    // BDD, modifiable dans /settings via I18N-B). Fallback `navigator.language`
    // sur les pages publiques (login, invite) où /api/moi répond 302 vers
    // /login.html — le content-type HTML est notre signal d'auth absente.
    async function detecterLangue() {
        try {
            const rep = await fetch('/api/moi', { credentials: 'same-origin' });
            const ct = rep.headers.get('content-type') || '';
            if (rep.ok && ct.includes('application/json')) {
                const data = await rep.json();
                if (data && LOCALES_VALIDES.has(data.locale)) return data.locale;
            }
        } catch (_) {
            // ignore — fallback navigateur ci-dessous
        }
        const nav = (typeof navigator !== 'undefined' && navigator.language) || '';
        return nav.toLowerCase().startsWith('fr') ? 'fr' : 'en';
    }

    // Initialisée dans init() une fois la détection résolue. Tant que le
    // boot n'est pas terminé, langueActive() renvoie null — les callers
    // sensibles doivent `await window.I18nPrete` avant de lire la valeur.
    let LANGUE_ACTIVE = null;

    // Normalise une chaîne : minuscule + suppression accents + trim.
    function normaliser(s) {
        return String(s || '')
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .trim();
    }

    // Reverse map : nom normalisé (de n'importe quelle langue) → slug canonique.
    // Construite au chargement, utilisée ensuite en synchrone.
    const reverseMap = new Map();

    // Dictionnaire de la langue active, alimenté au boot. Tant qu'il est vide,
    // `traduire(cle, fallback)` retombe sur fallback (pas de blocage UI).
    let dictionnaireActif = {};

    async function chargerLangue(url) {
        const rep = await fetch(url);
        if (!rep.ok) throw new Error(`Lang load failed: ${url} → ${rep.status}`);
        return rep.json();
    }

    function ingererClasses(dict) {
        for (const [cle, valeur] of Object.entries(dict)) {
            // Reverse map des classes : on ne traite que les clés BOSCO.Class.<slug>
            const match = cle.match(/^BOSCO\.Class\.(.+)$/);
            if (!match) continue;
            const slugCanonique = match[1]; // déjà en français normalisé
            reverseMap.set(normaliser(valeur), slugCanonique);
            // Au passage : le slug lui-même doit aussi pointer sur lui-même,
            // au cas où Foundry sort directement "barde" sans variante.
            reverseMap.set(slugCanonique, slugCanonique);
        }
    }

    window.I18nPrete = (async function init() {
        try {
            // Détection langue et chargement des deux dicts en parallèle :
            // on a besoin des deux quoi qu'il arrive (reverse map des classes
            // Foundry doit matcher FR et EN), et la détection async n'est
            // pas bloquante pour le fetch des JSON.
            const [langue, fr, en] = await Promise.all([
                detecterLangue(),
                chargerLangue('/lang/fr.json'),
                chargerLangue('/lang/en.json'),
            ]);
            LANGUE_ACTIVE = langue;
            // Les deux dicts alimentent la reverse map (n'importe quelle langue
            // Foundry doit pouvoir matcher).
            ingererClasses(fr);
            ingererClasses(en);
            // Aliases finaux (Prêtre normalisé → clerc, etc.)
            for (const [alias, canonique] of Object.entries(ALIAS_SUPPLEMENTAIRES)) {
                reverseMap.set(alias, canonique);
            }
            // Dictionnaire actif pour les traductions de libellés UI.
            dictionnaireActif = LANGUE_ACTIVE === 'fr' ? fr : en;
            // WCAG 3.1.1 : aligne l'attribut lang du document sur la langue
            // résolue (le HTML statique est figé sur lang="fr").
            if (typeof document !== 'undefined' && LANGUE_ACTIVE) {
                document.documentElement.lang = LANGUE_ACTIVE;
            }
        } catch (erreur) {
            console.error('i18n : chargement langues échoué', erreur);
            // En cas d'échec, slugCanoniqueClasse renverra '' et traduire
            // retombera sur fallback. Pas de blocage de la page.
        }
        // Auto-application sur le document. Si le DOM n'est pas encore prêt
        // (cas typique quand i18n.js est dans <head>), on attend
        // DOMContentLoaded. Les caches injectés après le boot doivent
        // appeler appliquerI18n() manuellement.
        appliquerAuDocumentQuandPret();
    })();

    function appliquerAuDocumentQuandPret() {
        if (typeof document === 'undefined') return;
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => window.appliquerI18n(document), { once: true });
        } else {
            window.appliquerI18n(document);
        }
    }

    // API publique : nom Foundry → slug canonique, ou '' si inconnu.
    window.slugCanoniqueClasse = function (nomFoundry) {
        const norm = normaliser(nomFoundry);
        if (!norm) return '';
        return reverseMap.get(norm) || '';
    };

    // API publique : clé i18n → libellé dans la langue active, ou `fallback`
    // si la clé est absente ou si le dict n'est pas encore chargé. Le fallback
    // est obligatoire pour rester safe avant que `I18nPrete` ne soit résolu
    // (cas rare : code synchrone au tout début du boot).
    window.traduire = function (cle, fallback) {
        const val = dictionnaireActif[cle];
        return typeof val === 'string' && val ? val : fallback || '';
    };

    // API publique : variante avec interpolation. Le template contient
    // `{cle}` (mot simple sans espace ni point), remplacé par params[cle].
    // Les placeholders absents de `params` sont remplacés par chaîne vide.
    window.traduireAvec = function (cle, fallback, params) {
        const tpl = window.traduire(cle, fallback);
        if (!params) return tpl;
        return tpl.replace(/\{(\w+)\}/g, (_, k) => (params[k] != null ? String(params[k]) : ''));
    };

    // API publique : parcourt `racine` (par défaut `document`) et applique
    // les traductions sur :
    //   - `data-i18n="cle"` → remplace `textContent`
    //   - `data-i18n-attr="attr:cle"` → remplace l'attribut HTML.
    //     Plusieurs paires séparées par `;` acceptées : "aria-label:foo;title:bar".
    //
    // Stratégie de fallback : si la clé est absente du dictionnaire, on garde
    // la valeur DOM existante (texte ou attribut). Conséquence : on peut
    // ajouter `data-i18n=…` à du HTML déjà écrit en FR sans rien casser tant
    // que la clé n'existe pas encore dans le dict.
    //
    // À rappeler manuellement sur tout DOM injecté après le boot (cartes
    // dashboard, modales, fragments rendus en JS).
    window.appliquerI18n = function (racine) {
        const r = racine || (typeof document !== 'undefined' ? document : null);
        if (!r || !r.querySelectorAll) return;

        r.querySelectorAll('[data-i18n]').forEach((el) => {
            const cle = el.dataset.i18n;
            if (!cle) return;
            const ancien = el.textContent;
            const trad = window.traduire(cle, ancien);
            if (trad && trad !== ancien) el.textContent = trad;
        });

        r.querySelectorAll('[data-i18n-attr]').forEach((el) => {
            const spec = el.dataset.i18nAttr;
            if (!spec) return;
            spec.split(';').forEach((paire) => {
                const idx = paire.indexOf(':');
                if (idx <= 0) return;
                const attr = paire.slice(0, idx).trim();
                const cle = paire.slice(idx + 1).trim();
                if (!attr || !cle) return;
                const ancien = el.getAttribute(attr) || '';
                const trad = window.traduire(cle, ancien);
                if (trad && trad !== ancien) el.setAttribute(attr, trad);
            });
        });
    };

    // Exposé pour debug/tests : langue détectée au boot. Renvoie null tant
    // que `await window.I18nPrete` n'a pas été résolu.
    window.langueActive = function () {
        return LANGUE_ACTIVE;
    };
})();
